// @ts-strict-ignore
/**
 * Global Variants Bulk Edit Page
 *
 * This page uses: productVariants(globalVariant: true) query
 * And mutation: productVariantBulkUpdate(variants: [...]) WITHOUT product ID
 */

import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import useAppChannel from "@dashboard/components/AppLayout/AppChannelContext";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { productListUrl, productVariantEditUrl } from "../urls";
import ProductVariants from "../components/ProductVariants";
import {
  DatagridChangeOpts,
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { useIntl } from "react-intl";
import { Option } from "@saleor/macaw-ui-next";
import { useSearchAttributeValuesSuggestions } from "@dashboard/searches/useAttributeValueSearch";
import { Box, Text, Button } from "@saleor/macaw-ui-next";
import {
  globalProductVariantsQuery,
  globalVariantBulkUpdateMutation,
} from "../queries.global-variants";
import { useProductVariantBulkDeleteMutation } from "@dashboard/graphql";
import { useLocation } from "react-router";
import { configurationMenuUrl } from "@dashboard/configuration";

const BulkEditGlobalVariantsPage: React.FC = () => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const location = useLocation();
  const { availableChannels } = useAppChannel(false);

  const [variantChanges, setVariantChanges] = React.useState<DatagridChangeOpts | null>(null);
  const [errors, setErrors] = React.useState([]);

  // Initialize datagrid state (required for Datagrid component)
  const datagridState = useDatagridChangeState();

  // API CALL 1: Fetch ALL global variants with pagination
  // Query: productVariants(filter: { globalVariant: true })
  const [allData, setAllData] = React.useState<any>(null);
  const [isFetchingAll, setIsFetchingAll] = React.useState(true);

  const isFromConfiguration = Boolean((location as any)?.state?.fromConfiguration);
  const backHref = !isFromConfiguration ? configurationMenuUrl : productListUrl();
  
  const { data, loading, refetch, fetchMore } = useQuery(globalProductVariantsQuery, {
    variables: {
      first: 100,
    },
    onCompleted: async initialData => {
      console.log("=== PAGINATION DEBUG ===");
      console.log("Initial data loaded:", initialData?.productVariants?.edges?.length, "variants");

      // Check if there are more pages
      const hasNextPage = initialData?.productVariants?.pageInfo?.hasNextPage;
      const endCursor = initialData?.productVariants?.pageInfo?.endCursor;

      console.log("Has next page?", hasNextPage);
      console.log("End cursor:", endCursor);

      if (!hasNextPage) {
        console.log("No more pages, setting final data");
        setAllData(initialData);
        setIsFetchingAll(false);
        return;
      }

      // Fetch all remaining pages
      let currentData = initialData;
      let currentCursor = endCursor;
      let hasMore = hasNextPage;
      let pageCount = 1;

      console.log("Starting to fetch additional pages...");

      while (hasMore) {
        try {
          pageCount++;
          console.log(`Fetching page ${pageCount}...`);

          const moreData: any = await fetchMore({
            variables: {
              first: 100,
              after: currentCursor,
            },
          });

          console.log("Received data:", moreData);
          console.log("New variants:", moreData.data?.productVariants?.edges?.length);

          // Merge the data
          currentData = {
            ...currentData,
            productVariants: {
              ...currentData.productVariants,
              edges: [
                ...currentData.productVariants.edges,
                ...(moreData.data?.productVariants?.edges || []),
              ],
              pageInfo: moreData.data?.productVariants?.pageInfo,
            },
          };

          console.log("Total variants so far:", currentData.productVariants.edges.length);

          hasMore = moreData.data?.productVariants?.pageInfo?.hasNextPage || false;
          currentCursor = moreData.data?.productVariants?.pageInfo?.endCursor;

          console.log("Has more pages?", hasMore);
        } catch (error) {
          console.error("Error fetching more variants:", error);
          break;
        }
      }

      console.log("=== PAGINATION COMPLETE ===");
      console.log("Total pages fetched:", pageCount);
      console.log("Total variants:", currentData.productVariants.edges.length);

      setAllData(currentData);
      setIsFetchingAll(false);
    },
  });

  // Add bulk delete mutation
  const [bulkDeleteVariants, { loading: bulkDeleteLoading }] = useProductVariantBulkDeleteMutation({
    onCompleted: async data => {
      if (data?.productVariantBulkDelete?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(
            {
              id: "variants.bulkDelete.success",
              defaultMessage: "{count} variant(s) deleted successfully",
            },
            { count: data.productVariantBulkDelete.count },
          ),
        });
        // Refetch variants data
        await refetch();
        setVariantChanges(null);
      } else {
        const errors = data?.productVariantBulkDelete?.errors || [];
        setErrors(errors);
        notify({
          status: "error",
          text: "Some variants failed to delete. Check the errors.",
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message,
      });
    },
  });

  const handleBulkDeleteVariants = React.useCallback(
    async (variantIds: string[]) => {
      if (variantIds.length === 0) return;

      await bulkDeleteVariants({
        variables: {
          ids: variantIds,
        },
      });
    },
    [bulkDeleteVariants],
  );

  // Extract variants from productVariants query (use allData which contains all pages)
  const allVariants = React.useMemo(() => {
    const dataToUse = allData || data;
    if (!dataToUse?.productVariants?.edges) return [];

    try {
      return dataToUse.productVariants.edges.map((edge: any) => {
        const node = edge.node;
        // Ensure the variant has the required structure matching ProductDetailsVariantFragment
        return {
          __typename: "ProductVariant",
          ...node,
          // Ensure all required fields exist
          id: node.id || "",
          name: node.name || "",
          sku: node.sku || null,
          trackInventory: node.trackInventory ?? true,
          quantityLimitPerCustomer: node.quantityLimitPerCustomer || null,
          // Make sure channelListings is always an array with proper structure
          channelListings: (node.channelListings || []).map((listing: any) => ({
            __typename: "ProductVariantChannelListing",
            ...listing,
            id: listing.id || "",
            channel: {
              __typename: "Channel",
              ...listing.channel,
            },
            price: listing.price
              ? {
                __typename: "Money",
                ...listing.price,
              }
              : null,
            costPrice: listing.costPrice
              ? {
                __typename: "Money",
                ...listing.costPrice,
              }
              : null,
          })),
          // Make sure attributes is always an array
          attributes: (node.attributes || []).map((attr: any) => ({
            __typename: "SelectedAttribute",
            ...attr,
            attribute: {
              __typename: "Attribute",
              ...attr.attribute,
            },
            values: (attr.values || []).map((val: any) => ({
              __typename: "AttributeValue",
              ...val,
            })),
          })),
          // Make sure media is an array
          media: (node.media || []).map((m: any) => ({
            __typename: "ProductMedia",
            ...m,
          })),
          // Make sure stocks is an array
          stocks: (node.stocks || []).map((stock: any) => ({
            __typename: "Stock",
            ...stock,
            warehouse: {
              __typename: "Warehouse",
              ...stock.warehouse,
            },
          })),
          // Ensure weight exists
          weight: node.weight
            ? {
              __typename: "Weight",
              ...node.weight,
            }
            : null,
          // Ensure preorder exists
          preorder: node.preorder
            ? {
              __typename: "PreorderData",
              ...node.preorder,
            }
            : null,
        };
      });
    } catch (error) {
      console.error("Error mapping variants:", error);
      return [];
    }
  }, [allData, data]);

  // Get variant attributes from ALL variants (not just first one)
  // Since global variants can come from different products with different attributes
  const variantAttributes = React.useMemo(() => {
    if (allVariants.length === 0) return [];

    // Collect all unique attributes from all variants
    const attributesMap = new Map();

    allVariants.forEach((variant: any) => {
      // For regular variants (with product), use product.productType.variantAttributes
      if (variant?.product?.productType?.variantAttributes) {
        const attrs = variant.product.productType.variantAttributes;
        attrs.forEach((attr: any) => {
          if (!attributesMap.has(attr.id)) {
            attributesMap.set(attr.id, attr);
          }
        });
      }
      // For global variants (no product), build from variant.attributes
      else if (variant?.attributes && variant.attributes.length > 0) {
        variant.attributes.forEach((selectedAttr: any) => {
          const attr = selectedAttr.attribute;
          if (attr && !attributesMap.has(attr.id)) {
            // Build attribute definition from the attribute data
            attributesMap.set(attr.id, {
              id: attr.id,
              name: attr.name,
              slug: attr.slug,
              inputType: attr.inputType,
              // Add other fields that might be needed
              entityType: attr.entityType || null,
              valueRequired: attr.valueRequired || false,
              unit: attr.unit || null,
              choices: { edges: [] }, // Empty choices for now
            });
          }
        });
      }
    });

    const allAttributes = Array.from(attributesMap.values());

    console.log("Built variant attributes:", allAttributes.length, "attributes");
    console.log(
      "Attributes:",
      allAttributes.map(a => a.name),
    );

    return allAttributes;
  }, [allVariants]);

  // Map channels for pricing
  const channels = React.useMemo(
    () =>
      availableChannels.map(channel => ({
        id: channel.id,
        name: channel.name,
        currency: channel.currencyCode, // <‑ rename the property
      })),
    [availableChannels],
  );

  // API CALL 2: Update variants
  // Mutation: productVariantBulkUpdate(variants: [...]) - NO product ID
  const [updateVariants, { loading: updateLoading }] = useMutation(
    globalVariantBulkUpdateMutation,
    {
      onCompleted: data => {
        if (data.productVariantBulkUpdate.errors.length === 0) {
          notify({
            status: "success",
            text: intl.formatMessage(commonMessages.savedChanges),
          });
          refetch();
          setVariantChanges(null);
          setErrors([]);
        } else {
          setErrors(data.productVariantBulkUpdate.errors);
          notify({
            status: "error",
            text: "Some variants failed to update. Check the errors.",
          });
        }
      },
      onError: error => {
        notify({
          status: "error",
          text: error.message,
        });
      },
    },
  );

  const handleChange = (data: DatagridChangeOpts) => {
    console.log("=== DATAGRID CHANGE ===");
    console.log("Change data:", data);
    console.log("Updates:", data?.updates);
    setVariantChanges(data);
  };

  // Log when variantChanges updates
  React.useEffect(() => {
    if (variantChanges) {
      console.log("=== VARIANT CHANGES UPDATED ===");
      console.log("variantChanges:", variantChanges);
      console.log("Should show Savebar now!");
    }
  }, [variantChanges]);

  const handleSave = async () => {
    console.log("=== SAVING CHANGES ===");
    console.log("variantChanges:", variantChanges);

    if (!variantChanges || allVariants.length === 0) return;

    // Build update input from datagrid changes
    const variantsToUpdate: any[] = [];

    // Process each changed cell
    Object.entries(variantChanges.updates).forEach(([coordinates, value]: [string, any]) => {
      console.log("Processing change:", coordinates, "=", value);

      const [colIndex, rowIndex] = coordinates.split(",").map(Number);
      const variant = allVariants[rowIndex];

      if (!variant) {
        console.log("Variant not found for row:", rowIndex);
        return;
      }

      console.log("Variant:", variant.name, variant.id);

      // Find or create update entry for this variant
      let updateEntry = variantsToUpdate.find((v: any) => v.id === variant.id);
      if (!updateEntry) {
        updateEntry = { id: variant.id };
        variantsToUpdate.push(updateEntry);
      }

      // Map column to field (simplified - you may need to enhance this)
      const columnKeys = Object.keys(variantChanges.updates);
      const columnId = columnKeys[colIndex];

      console.log("Column ID:", columnId);

      if (columnId?.startsWith("channel:")) {
        const channelId = columnId.replace("channel:", "");
        console.log("Updating price for channel:", channelId, "to:", value);
        if (!updateEntry.channelListings) {
          updateEntry.channelListings = [];
        }
        updateEntry.channelListings.push({
          channelId,
          price: parseFloat(value) || 0,
        });
      } else if (columnId === "name") {
        updateEntry.name = value;
      } else if (columnId === "sku") {
        updateEntry.sku = value;
      }
    });

    if (variantsToUpdate.length === 0) {
      notify({
        status: "info",
        text: "No changes to save",
      });
      return;
    }

    // Call mutation WITHOUT product ID
    await updateVariants({
      variables: {
        variants: variantsToUpdate,
      },
    });
  };

  const getAttributeValuesSuggestions = useSearchAttributeValuesSuggestions();

  const handleAttributeValuesSearch = async (id: string, query: string): Promise<Option[]> => {
    const results = await getAttributeValuesSuggestions(id, query);
    return results.map(r => ({
      label: r.label,
      value: r.value,
    }));
  };

  const isSaveDisabled = !variantChanges || updateLoading;

  // Log data for debugging
  React.useEffect(() => {
    if (data || allData) {
      console.log("=== GLOBAL VARIANTS DEBUG ===");
      console.log("Using allData?", !!allData);
      console.log("Raw API Data (first page):", data);
      console.log("All Data (merged):", allData);
      console.log("Extracted Variants Count:", allVariants.length);
      console.log("First Variant Sample:", allVariants[0]);
      console.log("Last Variant Sample:", allVariants[allVariants.length - 1]);
      console.log("Variant Attributes:", variantAttributes);
      console.log("Variant Attributes Length:", variantAttributes?.length);
      console.log("Channels:", channels);
      console.log("=== ABOUT TO RENDER ProductVariants ===");
      console.log("Passing variantAttributes:", variantAttributes);
    }
  }, [data, allData, allVariants, variantAttributes, channels]);

  if (loading || isFetchingAll) {
    return (
      <DetailPageLayout gridTemplateColumns={1}>
        <WindowTitle title="Edit Global Variants" />
        <TopNav title="Edit Global Variants" href={backHref} />
        <DetailPageLayout.Content>
          <Box paddingX={6} paddingY={8}>
            <Text>Loading all global variants...</Text>
            {allVariants.length > 0 && (
              <Text style={{ marginTop: 8, fontSize: 14, color: "#666" }}>
                Loaded {allVariants.length} variants...
              </Text>
            )}
          </Box>
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  if (allVariants.length === 0) {
    return (
      <DetailPageLayout gridTemplateColumns={1}>
        <WindowTitle title="Edit Global Variants" />
        <TopNav title="Edit Global Variants" href={backHref}>
          <Button
            onClick={() => navigate("/products/variants/add-variant")}
            data-test-id="create-global-variant-button"
          >
            Create Global Variant
          </Button>
        </TopNav>
        <DetailPageLayout.Content>
          <Box paddingX={6} paddingY={8}>
            <Text>No global variants are available right now.</Text>
          </Box>
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  // Wrap in try-catch for rendering
  try {
    return (
      <DetailPageLayout gridTemplateColumns={1}>
        <WindowTitle title="Edit Global Variants" />
        <TopNav title="Edit Global Variants" href={backHref}>
          <Button
            onClick={() => navigate("/products/variants/add-variant")}
            data-test-id="create-global-variant-button"
          >
            Create Global Variant
          </Button>
        </TopNav>
        <DetailPageLayout.Content>
          <Box paddingX={6} paddingY={2}>
            <Text>Showing all {allVariants.length} global variants</Text>
          </Box>
          {channels.length > 0 ? (
            <DatagridChangeStateContext.Provider value={datagridState}>
              <ProductVariants
                productId=""
                productName="Global Variants"
                errors={errors}
                channels={channels}
                limits={{
                  __typename: "LimitInfo",
                  currentUsage: {
                    __typename: "Limits",
                    productVariants: allVariants.length,
                  },
                  allowedUsage: {
                    __typename: "Limits",
                    productVariants: 1000,
                  },
                }}
                variants={allVariants as any}
                variantAttributes={variantAttributes}
                onAttributeValuesSearch={handleAttributeValuesSearch}
                onChange={handleChange}
                onRowClick={id =>
                  navigate(productVariantEditUrl(id), {
                    state: {
                      fromBulkEditGlobalVariants: true,
                    },
                  })
                }
                onBulkDelete={handleBulkDeleteVariants} // Add this prop
                initialFullscreen={true}
              />
            </DatagridChangeStateContext.Provider>
          ) : (
            <Box paddingX={6} paddingY={4}>
              <Text>Loading variant configuration...</Text>
            </Box>
          )}
        </DetailPageLayout.Content>

        {variantChanges && (
          <Savebar>
            <Savebar.Spacer />
            <Savebar.CancelButton
              onClick={() => {
                setVariantChanges(null);
                refetch();
              }}
            />
            <Savebar.ConfirmButton
              transitionState={updateLoading || bulkDeleteLoading ? "loading" : "default"}
              onClick={handleSave}
              disabled={isSaveDisabled || bulkDeleteLoading}
            />
          </Savebar>
        )}
      </DetailPageLayout>
    );
  } catch (error) {
    console.error("Error rendering BulkEditGlobalVariantsPage:", error);
    return (
      <DetailPageLayout gridTemplateColumns={1}>
        <WindowTitle title="Edit Global Variants" />
        <TopNav title="Edit Global Variants" href={backHref} />
        <DetailPageLayout.Content>
          <Box paddingX={6} paddingY={8}>
            <Text>Unexpected error while rendering global variants. Check console logs.</Text>
          </Box>
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }
};

export default BulkEditGlobalVariantsPage;
